import React, { Suspense, useEffect, useState } from "react";
import { toast } from "react-toastify";
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { LoadingButton } from "@mui/lab";
import { CircularProgress } from '@mui/material'
import { GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import VouchersAPI from "api/faber/VouchersAPI";
import MUIDataGrid from "common/AbstractDataGrid/DataGridMUI";
import ColorBox from "common/Box/ColorWhiteBox";
import PageLayoutV2 from "common/Layout/Page/PageLayoutV2";
import LayoutSkeleton from "common/Skeletons/LayoutSkeleton";
import NormalText from "common/Typography/NormalText";
import OrbitSession from "hooks/useCookies";

function VouchersDashboard() {
  const faberToken = OrbitSession.get('faberToken')

  const [uploadStatus, setUploadStatus] = useState<string>('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [uploadedObject, setUploadedObject] = useState<any>(null)
  // const [issuedAt, setIssuedAt] = useState<Date | null>(null);

  interface VoucherRowDataType {
    vendor: string
    name: string
    voucher_sku: string
    used_count: number
    available_count: number
    expired_count: number
  }

  const columns: GridColDef[] = [
    // Vendor 
    {
      field: 'vendor',
      headerName: 'Vendor',
      width: 150
    },
    // Voucher Name
    {
      field: 'name',
      headerName: 'Voucher Name',
      width: 150
    },
    // Voucher SKU
    {
      field: 'voucherSku',
      headerName: 'Voucher SKU',
      width: 50
    },
    // Used Count
    {
      field: 'used',
      headerName: 'Used Count',
      width: 50
    },
    // Available Count
    {
      field: 'available',
      headerName: 'Available Count',
      width: 50
    },
    // Expired Count
    {
      field: 'expired',
      headerName: 'Expired Count',
      width: 50
    }
  ]

  const [voucherDashboardData, setVoucherDashboardData] = useState<VoucherRowDataType[]>([])
  const { data: voucherData } = useQuery(
    ['voucherData'],
    async () => {
      return VouchersAPI.getDashboardVouchers(faberToken)
    }
  )

  const {
    mutate: uploadVoucherFile,
    // isLoading: isUploadVoucherFileLoading
  } = useMutation(VouchersAPI.uploadVouchers, {
    onSuccess: () => {
      toast.success('Successfully uploaded CSV')
    },
    onError: () => {
      toast.error('There is a problem trying to upload the voucher file to the server')
    },
  })

  const onFileUpload = async (file) => {
    const reader = new FileReader()
    reader.onload = async () => {
      // Set the uploaded file
      setUploadedObject(file)
      setUploadStatus('PENDING')
    }

    reader.readAsText(file)
  }

  useEffect(() => {
    if (uploadStatus === 'PENDING') {
      // Create a form and append the file to the data
      // when the reader finishes reading
      const formData = new FormData()
      formData.append('file', uploadedObject)

      // if (issuedAt == null) {
      //   toast.error('Please select the issued at date from the date picker')
      //   return
      // }
      // formData.append('issuedAt', issuedAt?.toDateString())

      // Upload this form 
      uploadVoucherFile({
        faberToken,
        uploadObject: formData
      })
    }
  }, [uploadStatus, uploadedObject])

  useEffect(() => {
    if (voucherData?.data) {
      const voucherDashboardData = voucherData.data.map((log) => {
        return {
          vendor: log.vendor,
          name: log.name,
          voucherSku: log.voucherSku,
          used: log.usedCount,
          available: log.availableCount,
          expired: log.expiredCount
        }
      })
      setVoucherDashboardData(voucherDashboardData)
    }
  }, [voucherData])

  return (
    <Suspense fallback={<LayoutSkeleton />}>

      <PageLayoutV2 header="Manage Voucher">
        <ColorBox>
          {/* Date Picker */}
          {/* <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Issued at"
              value={issuedAt}
              onChange={(newValue) => {
                setIssuedAt(newValue);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider> */}

          {/* Upload CSV button*/}
          <LoadingButton
            variant='outlined'
            component='label'
            sx={{ fontSize: '12px', p: 3, background: 'white' }}
            startIcon={<FileUploadIcon />}
            loadingIndicator={<CircularProgress />}
          >
            Upload file for jetpass vouchers
            <input
              type='file'
              hidden
              accept='.csv'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!e.target.files) return
                onFileUpload(e.target.files[0])
              }}
            />
          </LoadingButton>
        </ColorBox>
      </PageLayoutV2>

      <ColorBox sx={{ minWidth: '1000px' }}>
        <NormalText isBold>Vouchers Dashboard</NormalText>
        <MUIDataGrid
          columns={columns}
          rows={voucherDashboardData}
          getRowId={(row: VoucherRowDataType) => `${row.name}_${row.voucher_sku}`}
        />
      </ColorBox>
    </Suspense>
  )
}

export default VouchersDashboard